import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, PawPrint, ArrowLeft } from "lucide-react";
import { api } from "@/lib/helpers";

const KIND_MESSAGES = {
  donation: { title: "Your gift is on its way up the mountain!", body: "Thank you for supporting our dogs and cats. A receipt is waiting in your email — 100% of it goes to the animals." },
  sponsorship: { title: "You're a sponsor!", body: "Thank you for sponsoring one of our residents — expect pawdate updates and photos in your inbox." },
  wishlist: { title: "The pups and kitties say thank you!", body: "Your wishlist item is claimed. We'll send drop-off or shipping details by email." },
  merch: { title: "Order received — thank you for repping the rescue!", body: "We're packing it up on the mountain. A confirmation is in your email." },
};

export default function PaymentResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(sessionId ? "checking" : "done");
  const [kind, setKind] = useState("donation");

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    const tick = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setKind(data.kind || "donation"); setState("paid"); return; }
        if (["failed", "expired"].includes(data.payment_status)) { setState("failed"); return; }
      } catch (e) { /* keep polling */ }
      tries += 1;
      if (tries > 10) { setState("timeout"); return; }
      setTimeout(tick, 2500);
    };
    tick();
  }, [sessionId]);

  const msg = KIND_MESSAGES[kind] || KIND_MESSAGES.donation;

  return (
    <div className="min-h-screen bg-pine-deep grain flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
        className="w-full max-w-lg rounded-3xl bg-sand p-8 sm:p-12 text-center shadow-2xl" data-testid={cancelled ? "payment-cancel-card" : "payment-result-card"}>
        {cancelled ? (
          <>
            <XCircle className="mx-auto h-14 w-14 text-stone-400" />
            <h1 className="mt-5 font-serif text-3xl text-charcoal">No worries — the mountain will wait.</h1>
            <p className="mt-3 text-bark">Your checkout was cancelled and nothing was charged. The dogs and cats will be here when you're ready.</p>
          </>
        ) : state === "paid" ? (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-pine" />
            <h1 className="mt-5 font-serif text-3xl text-charcoal" data-testid="payment-success-title">Thank you!</h1>
            <p className="mt-3 text-bark" data-testid="payment-success-body">{msg.body}</p>
          </>
        ) : state === "failed" || state === "timeout" ? (
          <>
            <XCircle className="mx-auto h-14 w-14 text-red-600" />
            <h1 className="mt-5 font-serif text-3xl text-charcoal">Payment didn't go through.</h1>
            <p className="mt-3 text-bark">If any amount was taken, it will be refunded automatically. Please try again.</p>
          </>
        ) : (
          <>
            <PawPrint className="mx-auto h-12 w-12 text-gold animate-pulse-soft" />
            <h1 className="mt-5 font-serif text-3xl text-charcoal">Confirming with the mountain…</h1>
            <p className="mt-3 text-bark">Checking your payment status. This only takes a moment.</p>
          </>
        )}
        <Link data-testid="payment-back-home" to="/"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-pine px-7 py-3.5 font-bold text-sand hover:bg-pine-mid transition-all hover:scale-[1.03]">
          <ArrowLeft className="h-4 w-4" /> Back to the sanctuary
        </Link>
      </motion.div>
    </div>
  );
}
